import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleCategory, deleteCategory, setRoundsDaysBeforeDate, setChatRestrictMinors, setChatMemberMessagingEnabled } from '../actions';
import { Button } from 'react-bootstrap';
import { GlgTooltipIcon } from '../../shared/components';
import AddMenuItemModal from './modals/add_menu_item_modal';
import AddSubcategoryModal from './modals/add_subcategory_modal';
import CategoryTable from './categories/category_table';
import MobilePreview from './mobile_preview';
import { useCategoryHandlers } from '../hooks/useCategoryHandlers';
import DirectoriesTable from './directories_table';

const AppDashboardLayout = () => {
  const buttons = useSelector(state => state.buttons);
  const directories = useSelector(state => state.directories);
  const clubLogo = useSelector(state => state.club_logo);
  const canDashboardItems = useSelector(state => state.dashboardItems);
  const clubName = useSelector(state => state.club_name);
  const roundsDaysBeforeDate = useSelector(state => state.rounds_days_before_date);
  const maxRoundsDaysBeforeDate = useSelector(state => state.max_rounds_days_before_date);
  const chatAvailable = useSelector(state => state.chat_available);
  const chatMemberMessagingEnabled = useSelector(state => state.chat_member_messaging_enabled);
  const chatRestrictMinors = useSelector(state => state.chat_restrict_minors);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState({});
  const categories = buttons.filter(btn => btn.parent_id === null);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  useEffect(() => {
    window.glg.app_state = {
      buttons,
      directories,
      rounds_days_before_date: roundsDaysBeforeDate,
      chat_restrict_minors: chatRestrictMinors,
      chat_member_messaging_enabled: chatMemberMessagingEnabled,
    };
  }, [buttons, directories, roundsDaysBeforeDate, chatRestrictMinors, chatMemberMessagingEnabled]);

  useEffect(() => {
    window.glg.is_dirty = false;

    const handleBeforeUnload = (e) => {
      if (window.glg.is_dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);


  const {
    handleSaveNewCategory,
    handleSaveNewSubcategory,
    handleUpdateCategory,
    handleUpdateSubcategory,
  } = useCategoryHandlers({ setShowMenuModal, setShowSubModal, setEditMode, setItemToEdit });

  const handleEditClick = (item) => {
    setItemToEdit(item);
    setEditMode(true);
    item.parent_id === null ? setShowMenuModal(true) : setShowSubModal(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteCategory(id));
  };

  const grouped = categories
    .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    .map(category => ({
      ...category,
      subcategories: buttons
        .filter(btn => btn.parent_id === category.id)
        .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    }));

  const toggleExpanded = id => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <React.Fragment>
      <AddMenuItemModal
        show={showMenuModal}
        onHide={() => {
          setShowMenuModal(false);
          setEditMode(false);
          setItemToEdit(null);
        }}
        onSave={editMode ? handleUpdateCategory : handleSaveNewCategory}
        initialData={itemToEdit}
        editMode={editMode}
      />

      <AddSubcategoryModal
        show={showSubModal}
        onHide={() => {
          setShowSubModal(false);
          setEditMode(false);
          setItemToEdit(null);
        }}
        onSave={editMode ? handleUpdateSubcategory : handleSaveNewSubcategory}
        parentCategories={categories}
        initialData={itemToEdit}
        editMode={editMode}
      />

      <div className="col-md-7 app_dashboard">

        <div className='page_title'>
          <div className="text after_text_1">{window.I18n.t('mobile_app_dashboard.home.menu_items')}</div>
        </div>

        {canDashboardItems && (
          <div>
            <div className='btsp_bullet_button pointer btsp_text_15' onClick={() => setShowMenuModal(true)}>
              <div className='icon'><i className='gga-circle-plus'></i></div>
              <div className='btsp_align-middle'>{window.I18n.t('mobile_app_dashboard.home.new_menu_item')}</div>
            </div>

            <div className='btsp_bullet_button pointer btsp_text_15' onClick={() => setShowSubModal(true)}>
              <div className='icon'><i className='gga-circle-plus'></i></div>
              <div className='btsp_align-middle'>{window.I18n.t('mobile_app_dashboard.home.new_submenu_item')}</div>
            </div>
          </div>
        )}

        <p className="text-muted">
          {window.I18n.t('mobile_app_dashboard.home.select_menu_and_submenu')}
        </p>

        <CategoryTable
          grouped={grouped}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          handleEditClick={handleEditClick}
          handleDelete={handleDelete}
          toggleCategory={toggleCategory}
        />

        <div className='page_title directories-title'>
          <div className="text after_text_1">{window.I18n.t('mobile_app_dashboard.home.directories')}</div>
        </div>

        <DirectoriesTable />

        <div className="page_title home-settings-title">
          <div className="text after_text_1">{!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.home_settings')}</div>
        </div>

        <p>
          {!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.rounds_before_date_prefix')}
          {' '}
          <input
            type="number"
            min="0"
            max={maxRoundsDaysBeforeDate}
            step={1}
            value={roundsDaysBeforeDate}
            onChange={(e) => {
              const val = Math.min(maxRoundsDaysBeforeDate, Math.max(0, parseInt(e.target.value, 10) || 0));
              dispatch(setRoundsDaysBeforeDate(val));
            }}
            style={{ width: '60px', display: 'inline-block' }}
          />
          {' '}
          {!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.rounds_before_date_suffix')}
        </p>

        {chatAvailable && (
          <div className="page_title chat-settings-title">
            <div className="text after_text_1">{!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.chat_settings')}</div>
          </div>
        )}

        {chatAvailable && (
          <div className="fieldset inline check">
            <label className="btsp_icheck_label no-padding-left">
              <div className={`icheckbox_square-orange btsp-icheck-xs icheck-item icheck ${chatMemberMessagingEnabled ? 'checked' : ''}`}>
                <input
                  id="chat_member_messaging_enabled"
                  type="checkbox"
                  className="icheck-input"
                  checked={chatMemberMessagingEnabled}
                  onChange={(e) => dispatch(setChatMemberMessagingEnabled(e.target.checked))}
                />
              </div>
              <span>{!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.member_messaging')}</span>
            </label>
            <GlgTooltipIcon tooltip={!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.member_messaging_help')} />
          </div>
        )}

        {chatAvailable && (
          <div className="fieldset inline check">
            <label className="btsp_icheck_label no-padding-left">
              <div className={`icheckbox_square-orange btsp-icheck-xs icheck-item icheck ${chatRestrictMinors ? 'checked' : ''}`}>
                <input
                  id="checkbox_chat_restrict_minors"
                  type="checkbox"
                  className="icheck-input"
                  checked={chatRestrictMinors}
                  onChange={(e) => dispatch(setChatRestrictMinors(!chatRestrictMinors))}
                />
              </div>
              <span>{!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.restrict_minor_chat')}</span>
            </label>
          </div>
        )}
      </div>

      <MobilePreview grouped={grouped} clubLogo={clubLogo} clubName={clubName} />
    </React.Fragment>
  );
};

export default AppDashboardLayout;
